import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Briefcase, MapPin, Calendar, Clock, Building, 
  ArrowLeft, AlertCircle, CheckCircle, RefreshCw,
  ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

// Status color mapping
const STATUS_COLORS = {
  application_submitted: 'bg-blue-100 text-blue-800 border-blue-200',
  under_review: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  shortlisted: 'bg-purple-100 text-purple-800 border-purple-200',
  interview_stage: 'bg-orange-100 text-orange-800 border-orange-200',
  offer_stage: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  offer_accepted: 'bg-green-100 text-green-800 border-green-200',
  not_proceeding: 'bg-gray-100 text-gray-700 border-gray-200',
};

const STATUS_ICONS = {
  application_submitted: CheckCircle,
  under_review: RefreshCw,
  shortlisted: CheckCircle,
  interview_stage: Calendar,
  offer_stage: Briefcase,
  offer_accepted: CheckCircle,
  not_proceeding: AlertCircle,
};

export default function CandidatePortalPage() {
  const { language, direction } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [applications, setApplications] = useState([]);
  const [expandedApp, setExpandedApp] = useState(null);
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('no_token');
      setLoading(false);
      return;
    }
    
    verifyAndFetch();
  }, [token]);

  const verifyAndFetch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Verify token
      const verifyRes = await fetch(`${API}/candidate/verify-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      if (!verifyRes.ok) {
        const err = await verifyRes.json();
        if (err.detail?.includes('expired')) {
          setError('expired');
        } else {
          setError('invalid');
        }
        setLoading(false);
        return;
      }
      
      const verifyData = await verifyRes.json();
      setEmail(verifyData.email);
      
      // Fetch applications
      const appsRes = await fetch(`${API}/candidate/applications?token=${token}&lang=${language}`);
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData.applications || []);
      } else {
        setError('fetch_failed');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('connection');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    const Icon = STATUS_ICONS[status] || CheckCircle;
    return <Icon className="h-4 w-4" />;
  };

  // Error states
  if (error === 'no_token') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white flex items-center justify-center p-4" dir={direction}>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-[#111827] mb-3">
              {language === 'ar' ? 'رابط الوصول مطلوب' : 'Access Link Required'}
            </h2>
            <p className="text-[#6B7280] mb-6">
              {language === 'ar' 
                ? 'تحتاج إلى رابط وصول لعرض طلباتك.'
                : 'You need an access link to view your applications.'
              }
            </p>
            <Link to="/candidate-access">
              <Button className="btn-primary">
                {language === 'ar' ? 'طلب رابط الوصول' : 'Request Access Link'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white flex items-center justify-center p-4" dir={direction}>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#111827] mb-3">
              {language === 'ar' ? 'انتهت صلاحية الرابط' : 'Link Expired'}
            </h2>
            <p className="text-[#6B7280] mb-6">
              {language === 'ar' 
                ? 'انتهت صلاحية رابط الوصول. يرجى طلب رابط جديد.'
                : 'Your access link has expired. Please request a new one.'
              }
            </p>
            <Link to="/candidate-access">
              <Button className="btn-primary">
                {language === 'ar' ? 'طلب رابط جديد' : 'Request New Link'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'invalid' || error === 'fetch_failed' || error === 'connection') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white flex items-center justify-center p-4" dir={direction}>
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-[#111827] mb-3">
              {language === 'ar' ? 'حدث خطأ' : 'Something Went Wrong'}
            </h2>
            <p className="text-[#6B7280] mb-6">
              {language === 'ar' 
                ? 'تعذر تحميل طلباتك. يرجى المحاولة مرة أخرى.'
                : 'We could not load your applications. Please try again.'
              }
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={verifyAndFetch} variant="outline">
                <RefreshCw className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إعادة المحاولة' : 'Try Again'}
              </Button>
              <Link to="/candidate-access">
                <Button variant="ghost" className="w-full">
                  {language === 'ar' ? 'طلب رابط جديد' : 'Request New Link'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white flex items-center justify-center" dir={direction}>
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-[#33AADD]/30 border-t-[#33AADD] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6B7280]">
            {language === 'ar' ? 'جاري التحميل...' : 'Loading your applications...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f9ff] to-white py-8 px-4" dir={direction} data-testid="candidate-portal">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-[#6B7280] hover:text-[#33AADD] mb-4 text-sm">
            <ArrowLeft className="h-4 w-4 me-1" />
            {language === 'ar' ? 'العودة للرئيسية' : 'Back to Home'}
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#111827]">
                {language === 'ar' ? 'طلباتي' : 'My Applications'}
              </h1>
              <p className="text-[#6B7280] mt-1">
                {email && (
                  <span>{language === 'ar' ? 'مرتبط بـ' : 'Linked to'} <strong>{email}</strong></span>
                )}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={verifyAndFetch}>
              <RefreshCw className="h-4 w-4 me-2" />
              {language === 'ar' ? 'تحديث' : 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Applications List */}
        {applications.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Briefcase className="h-12 w-12 text-[#E5E7EB] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#111827] mb-2">
                {language === 'ar' ? 'لا توجد طلبات' : 'No Applications Found'}
              </h3>
              <p className="text-[#6B7280] mb-6">
                {language === 'ar' 
                  ? 'لم نجد أي طلبات مرتبطة ببريدك الإلكتروني.'
                  : "We couldn't find any applications linked to your email."
                }
              </p>
              <Link to="/jobs">
                <Button className="btn-primary">
                  {language === 'ar' ? 'تصفح الوظائف' : 'Browse Jobs'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <Card 
                key={app.application_id} 
                className="overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`application-card-${app.application_id}`}
              >
                <CardContent className="p-0">
                  {/* Main Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#111827] text-lg mb-1">
                          {app.job?.title || 'Position'}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-[#6B7280]">
                          <span className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            {app.job?.company || 'WeDo HR Services'}
                          </span>
                          {app.job?.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {app.job.location}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badge */}
                      <Badge 
                        className={`${STATUS_COLORS[app.status?.status] || STATUS_COLORS.application_submitted} border flex items-center gap-1.5 px-3 py-1.5`}
                      >
                        {getStatusIcon(app.status?.status)}
                        {app.status?.label || 'Submitted'}
                      </Badge>
                    </div>
                    
                    {/* Status Description */}
                    <div className="mt-4 p-3 bg-[#F9FAFB] rounded-lg">
                      <p className="text-sm text-[#374151]">
                        {app.status?.description || ''}
                      </p>
                    </div>
                    
                    {/* Meta Info */}
                    <div className="flex items-center gap-4 mt-4 text-sm text-[#6B7280]">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {language === 'ar' ? 'تقدمت في' : 'Applied'} {formatDate(app.applied_at)}
                      </span>
                      {app.last_updated && app.last_updated !== app.applied_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {language === 'ar' ? 'آخر تحديث' : 'Updated'} {formatDate(app.last_updated)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Expandable History */}
                  {app.history && app.history.length > 1 && (
                    <>
                      <button
                        onClick={() => setExpandedApp(expandedApp === app.application_id ? null : app.application_id)}
                        className="w-full px-5 py-3 border-t bg-[#FAFAFA] flex items-center justify-between text-sm text-[#6B7280] hover:bg-[#F5F5F5] transition-colors"
                      >
                        <span>{language === 'ar' ? 'سجل الحالة' : 'Status History'}</span>
                        {expandedApp === app.application_id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                      
                      {expandedApp === app.application_id && (
                        <div className="px-5 py-4 border-t bg-white">
                          <div className="space-y-3">
                            {app.history.map((entry, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${
                                  idx === app.history.length - 1 ? 'bg-[#33AADD]' : 'bg-[#D1D5DB]'
                                }`} />
                                <span className="text-sm font-medium text-[#374151]">
                                  {entry.label}
                                </span>
                                {entry.date && (
                                  <span className="text-sm text-[#9CA3AF]">
                                    {formatDate(entry.date)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-[#9CA3AF]">
            {language === 'ar' 
              ? 'سيتواصل معك فريق التوظيف لدينا مباشرة بشأن أي خطوات تالية.'
              : 'Our recruitment team will contact you directly regarding any next steps.'
            }
          </p>
        </div>
      </div>
    </div>
  );
}
